import React, { FC } from 'react';
import * as Styled from './TicketCreate.styles';
import { CreateTicketParams, createTicketParamsSchema } from '@modules/ticket/model';
import { useTranslations } from '@shared/modules/translation/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import FormField from '@shared/modules/form/components/FormField';
import { Input } from '@styles/shared/form/Input';
import { useRouter } from 'next/router';
import { InternationalizeString } from '@shared/modules/translation/model';
import { getCurrentLocale, getInternationalizeStringValue } from '@shared/modules/translation/utils';
import { TextArea } from '@styles/shared/form/TextArea';
import { Profile } from '@modules/auth/model';

interface TicketCreateFormProps {
  loading: boolean;
  onSubmit: (params: CreateTicketParams) => void;
  serialId: string;
  answerId: string;
  diagnosticName: InternationalizeString;
  email: string;
  profile: Profile;
}

const TicketCreateForm: FC<TicketCreateFormProps> = ({
  loading,
  onSubmit,
  serialId,
  answerId,
  diagnosticName,
  email,
  profile,
}) => {
  const translations = useTranslations();
  const router = useRouter();

  const {
    handleSubmit,
    watch,
    formState: { errors },
    register,
  } = useForm<CreateTicketParams>({
    resolver: zodResolver(createTicketParamsSchema),
    defaultValues: {
      answerId: answerId,
      user: {
        lastName: profile.lastName ?? '',
        firstName: profile.firstName ?? '',
        companyName: profile.companyName,
        address: profile.address,
        phoneNumber: profile.phoneNumber,
      },
      comment: null,
    },
  });

  const comment = watch('comment');
  const nbChar = comment != null ? 2048 - comment.length : 2048;

  return (
    <Styled.TicketCreateFormContainer onSubmit={handleSubmit(onSubmit)} noValidate>
      <Styled.TicketCreateDoubleInput>
        <FormField label={translations.tickets.create.serialNumber} htmlFor="serialNumber">
          <Input id="serialNumber" value={serialId} disabled />
        </FormField>

        <FormField label={translations.tickets.create.diagnostic} htmlFor="diagnostic">
          <Input
            id="diagnostic"
            value={getInternationalizeStringValue(diagnosticName, getCurrentLocale(router))}
            disabled
          />
        </FormField>

        <FormField label={translations.tickets.create.companyName} htmlFor="companyName">
          <Input
            id="companyName"
            placeholder={translations.tickets.create.companyName}
            error={!!errors.user?.companyName}
            {...register('user.companyName')}
          />
        </FormField>
      </Styled.TicketCreateDoubleInput>

      <Styled.TicketCreateDoubleInput>
        <FormField label={translations.tickets.create.lastName} htmlFor="lastName" required>
          <Input
            id="lastName"
            placeholder={translations.tickets.create.lastNamePlaceholder}
            error={!!errors.user?.lastName}
            {...register('user.lastName')}
          />
        </FormField>

        <FormField label={translations.tickets.create.firstName} htmlFor="firstName" required>
          <Input
            id="firstName"
            placeholder={translations.tickets.create.firstNamePlaceholder}
            error={!!errors.user?.firstName}
            {...register('user.firstName')}
          />
        </FormField>

        <FormField label={translations.tickets.create.email} htmlFor="email" required>
          <Input id="email" value={email} disabled />
        </FormField>

        <FormField label={translations.tickets.create.phoneNumber} htmlFor="phoneNumber" required>
          <Input
            id="phoneNumber"
            placeholder={translations.tickets.create.phoneNumberPlaceholder}
            error={!!errors.user?.phoneNumber}
            {...register('user.phoneNumber')}
          />
        </FormField>

        <FormField label={translations.tickets.create.address} htmlFor="address" required>
          <Input
            id="address"
            placeholder={translations.tickets.create.addressPlaceholder}
            error={!!errors.user?.address?.firstLine}
            {...register('user.address.firstLine')}
          />
        </FormField>

        <FormField label={translations.tickets.create.postalCode} htmlFor="postalCode" required>
          <Input
            id="postalCode"
            placeholder={translations.tickets.create.postalCode}
            error={!!errors.user?.address?.postalCode}
            {...register('user.address.postalCode')}
          />
        </FormField>

        <FormField label={translations.tickets.create.city} htmlFor="city" required>
          <Input
            id="city"
            placeholder={translations.tickets.create.city}
            error={!!errors.user?.address?.city}
            {...register('user.address.city')}
          />
        </FormField>

        <FormField label={translations.tickets.create.country} htmlFor="country" required>
          <Input
            id="country"
            placeholder={translations.tickets.create.country}
            error={!!errors.user?.address?.country}
            {...register('user.address.country')}
          />
        </FormField>
      </Styled.TicketCreateDoubleInput>

      <Styled.TicketCreateCommentContainer label={translations.tickets.create.comment} htmlFor="comment">
        <TextArea
          id="comment"
          placeholder={translations.tickets.create.commentPlaceholder}
          error={!!errors.comment}
          {...register('comment')}
          maxLength={2048}
        />

        <Styled.TicketCreateCommentRemaining>
          {translations.tickets.create.remaining} {nbChar}
        </Styled.TicketCreateCommentRemaining>
      </Styled.TicketCreateCommentContainer>

      <Styled.TicketCreateRequired>{translations.tickets.create.required}</Styled.TicketCreateRequired>

      <Styled.TicketCreateActions>
        <Styled.TicketCreateButton type="submit" $loading={loading}>
          {translations.tickets.create.button}
        </Styled.TicketCreateButton>
      </Styled.TicketCreateActions>
    </Styled.TicketCreateFormContainer>
  );
};

export default TicketCreateForm;
